// NodeMissionTaskAttachmentPhotoThumb.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// Utilities
import { isSketchType } from 'utilities/sketchType';

// Components
import Icon from 'svg/Icon';

/**
 * NodeMissionTaskAttachmentPhotoThumb
 *
 * Square PHOTO / SKETCH thumbnail for the workflow task Attachments grid.
 *
 * @param {object} node - PHOTO or SKETCH node.
 * @param {function} [onClick]
 * @returns {ReactNode}
 */
const NodeMissionTaskAttachmentPhotoThumb = React.memo(({
  node,
  onClick,
}) => {
  const { theme } = useTheme();
  const [imageUrl, setImageUrl] = useState(null);

  const candidateUrls = useMemo(() => (
    [node?.url_thumb, node?.url_grid, node?.url_feed, node?.url, node?.coverUrl]
      .filter((value, index, arr) => (
        typeof value === 'string'
        && value.trim()
        && arr.indexOf(value) === index
      ))
  ), [node?.coverUrl, node?.url, node?.url_feed, node?.url_grid, node?.url_thumb]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const nodeKey = String(node?.key || '').trim() || null;

      for (let index = 0; index < candidateUrls.length; index += 1) {
        const url = await resolveImageUrl({
          nodeKey,
          url: candidateUrls[index],
          storagePath: null,
        });
        if (cancelled) return;
        if (url) {
          setImageUrl(url);
          return;
        }
      }

      const storagePath = String(node?.storagePath || node?.coverStoragePath || '').trim();
      if (storagePath) {
        const fallback = await resolveImageUrl({
          nodeKey,
          url: null,
          storagePath,
        });
        if (cancelled) return;
        setImageUrl(fallback || null);
        return;
      }

      setImageUrl(null);
    })();

    return () => {
      cancelled = true;
    };
  }, [candidateUrls, node?.coverStoragePath, node?.key, node?.storagePath]);

  const isSketch = isSketchType(node?.type);
  const label = String(node?.title || node?.fileName || (isSketch ? 'Sketch' : 'Photo')).trim()
    || (isSketch ? 'Sketch' : 'Photo');

  return (
    <button
      aria-label={label}
      className={`node-mission-task-attachments-photo-thumb${isSketch ? ' is-sketch' : ''}`}
      style={{ backgroundColor: isSketch ? '#ffffff' : theme.backgroundMuted }}
      title={label}
      type="button"
      onClick={() => onClick?.(node)}
    >
      {imageUrl ? (
        <img
          alt=""
          className="node-mission-task-attachments-photo-thumb-image"
          draggable={false}
          src={imageUrl}
        />
      ) : (
        <Icon
          color={theme.foregroundMuted}
          height={20}
          iconKey={isSketch ? 'draw' : 'photo'}
          width={20}
        />
      )}
    </button>
  );
});

NodeMissionTaskAttachmentPhotoThumb.displayName = 'NodeMissionTaskAttachmentPhotoThumb';

export default NodeMissionTaskAttachmentPhotoThumb;
